import { UseMutateFunction } from '@tanstack/react-query';
import { useTranslation } from '@packlink/translation-provider';
import {
    Button,
    ButtonColor,
    ButtonVariant,
    DialogActions,
    DialogContent,
    Radio,
    RadioGroup,
    Textarea,
    Typography,
} from '@shipengine/giger';
import { AmplitudeEvents, AmplitudeProperties } from '@constants/amplitude';
import { useAmplitude } from '@hooks';
import {
    getCancellationActionsStyles,
    getCancellationSurveyFormStyles,
    getCancellationSurveyRadioStyles,
} from './CancelDialogStyles';
import { ChangeEvent, useState } from 'react';
import { useRetentionDiscount } from '@Subscriptions/hooks/useRetentionDiscount';

type SubscriptionCancellationConfirmationProps = {
    planName: string;
    onClose: () => void;
    cancel: UseMutateFunction;
    isPending?: boolean;
    setShowDiscountDialog: (show: boolean) => void;
};

export function SubscriptionCancellationConfirmation({
    planName,
    onClose,
    cancel,
    isPending,
    setShowDiscountDialog,
}: SubscriptionCancellationConfirmationProps): JSX.Element {
    const { t } = useTranslation();
    const { sendAmplitudeEvent, sendAmplitudeClickEvent } = useAmplitude();
    const [motive, setMotive] = useState<string | undefined>();
    const [additionalInfo, setAdditionalInfo] = useState('');
    const { isEligibleForDiscount } = useRetentionDiscount();

    const cancelSubscription = () => {
        sendAmplitudeClickEvent(AmplitudeEvents.SUBSCRIPTION_CANCEL_CONFIRM, {
            [AmplitudeProperties.SUBSCRIPTION_CANCELLATION_MOTIVE]: motive,
            [AmplitudeProperties.SUBSCRIPTION_CANCELLATION_ADDITIONAL_INFO]: additionalInfo,
        });

        if (motive === 'too expensive' && isEligibleForDiscount) {
            setShowDiscountDialog(true);
        } else {
            cancel(undefined, {
                onSuccess: () => {
                    sendAmplitudeEvent(AmplitudeEvents.SUBSCRIPTION_CANCELLATION_CONFIRMED);
                },
            });
        }
    };

    const radioChecked = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setMotive(value);
    };

    const onTextareaChange = ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
        setAdditionalInfo(value);
    };

    return (
        <>
            <DialogContent css={getCancellationSurveyFormStyles}>
                <Typography variant="body1" component="p">
                    {t('subscriptions.cancellation-form.subtitle', { planName: planName })}
                </Typography>
                <RadioGroup
                    name="cancellationMotive"
                    value={motive}
                    onChange={radioChecked}
                    css={getCancellationSurveyRadioStyles}
                >
                    <Radio value="poor support">{t('subscriptions.cancellation-form.option-1')}</Radio>
                    <Radio value="seasonal shipping">{t('subscriptions.cancellation-form.option-2')}</Radio>
                    <Radio value="missing feature">{t('subscriptions.cancellation-form.option-3')}</Radio>
                    <Radio value="too expensive">{t('subscriptions.cancellation-form.option-4')}</Radio>
                    <Radio value="business closed">{t('subscriptions.cancellation-form.option-5')}</Radio>
                    <Radio value="tech problems">{t('subscriptions.cancellation-form.option-6')}</Radio>
                    <Radio value="other">{t('subscriptions.cancellation-form.option-7')}</Radio>
                </RadioGroup>
                <Textarea
                    name="text area"
                    label={t('subscriptions.cancellation-form.option-8')}
                    value={additionalInfo}
                    onChange={onTextareaChange}
                />
            </DialogContent>

            <DialogActions css={getCancellationActionsStyles}>
                <Button variant={ButtonVariant.OUTLINED} onClick={onClose} color={ButtonColor.ERROR}>
                    {t('subscriptions.cancellation-form.stay', { planName: planName })}
                </Button>
                <Button onClick={cancelSubscription} isLoading={isPending} color={ButtonColor.ERROR} disabled={!motive}>
                    {t('subscriptions.cancellation-form.go')}
                </Button>
            </DialogActions>
        </>
    );
}
